import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ChatStatus, OrderStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeRestaurant } from '@/contexts/user-context';
import {
  formatOfflineDataStatusDate,
  formatOrderDeliveryTime,
} from '@/utils/locations';
import { LocationPickerAccountFieldsFragment } from '@/types';
import { OfflineDataStatus } from '@/adapters/offline-mode';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { View } from 'react-native';

// Inactive chats are filtered out by the query
// Active chats should show order label instead
export const CHAT_STATUS_TRANSLATION_MAP: {
  [status in ChatStatus]?: string;
} = {
  NEW: i18n.t('new'),
  PROSPECT: i18n.t('prospect'),
};

const ORDER_STATUS_TRANSLATION_MAP: { [status in OrderStatus]?: string } = {
  [OrderStatus.Draft]: i18n.t('order_started'),
  [OrderStatus.InReview]: i18n.t('ready_for_review'),
};

interface Props {
  account?: LocationPickerAccountFieldsFragment;
  isSupplierUser: boolean;
  offlineDataStatus?: OfflineDataStatus;
  relationship: EmployeeRestaurant;
  showSupplierName?: boolean;
}

export const LocationPickerListItemSubtitle: React.FC<Props> = ({
  account,
  isSupplierUser,
  offlineDataStatus,
  relationship,
  showSupplierName,
}) => {
  const [styles, theme] = useStyles();
  const isOfflineModeEnabled = useOfflineModeEnabled();

  const { addressString, chatStatus, supplierName } = relationship;

  const lastPlacedOrder = account?.placed_orders[0];
  const unplacedOrder = account?.unplaced_orders[0];

  const latestOfflineSyncDate = useMemo(() => {
    if (!isOfflineModeEnabled || !offlineDataStatus) {
      return null;
    }

    const isLegacyDataStatusFormat = 'downloadedDate' in offlineDataStatus;
    if (isLegacyDataStatusFormat) {
      return new Date(offlineDataStatus.downloadedDate);
    }

    const latestSyncTimestamp = Object.values(offlineDataStatus).reduce(
      (latestTimestamp, status) => {
        const timestamp = new Date(status.downloadedDate);
        return Math.max(latestTimestamp, timestamp.getTime());
      },
      0,
    );

    return latestSyncTimestamp > 0 ? new Date(latestSyncTimestamp) : null;
  }, [isOfflineModeEnabled, offlineDataStatus]);

  const accountLabels = [];

  // Lead with the supplier name so it's clear which supplier each account is under
  if (showSupplierName && supplierName) {
    accountLabels.push(supplierName);
  }

  // Show chat status for supplier users if exists
  if (isSupplierUser) {
    const chatStatusLabel = CHAT_STATUS_TRANSLATION_MAP[chatStatus];
    if (chatStatusLabel) {
      accountLabels.push(chatStatusLabel);
    }
  } else if (unplacedOrder) {
    // Show order status for restaurants with an unplaced order (DRAFT or IN_REVIEW)
    const orderStatusLabel = unplacedOrder
      ? ORDER_STATUS_TRANSLATION_MAP[unplacedOrder.status as OrderStatus]
      : null;
    accountLabels.push(orderStatusLabel);
  }
  // Show last placed order for supplier users or restaurants without an unplaced order
  if (lastPlacedOrder && (isSupplierUser || !unplacedOrder)) {
    accountLabels.push(formatOrderDeliveryTime(lastPlacedOrder));
  }
  // Show most recent offline download date
  if (latestOfflineSyncDate) {
    accountLabels.push(formatOfflineDataStatusDate(latestOfflineSyncDate));
  }

  // Maintain stable subtitle height for programmatic scrolling using a non-breaking space
  const subtitleText = accountLabels.join(' • ') || '\u00A0';

  return (
    <>
      <Typography numberOfLines={1} variant="paragraph-md">
        {addressString}
      </Typography>
      <View style={styles.statusRow}>
        <Typography
          color={theme.sk.color.text.tertiary}
          numberOfLines={1}
          variant="paragraph-sm-600"
        >
          {subtitleText}
        </Typography>
      </View>
    </>
  );
};

const useStyles = createStyles(() => ({
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingVertical: 2,
  },
}));
